// src/admin/pages/UserActivityLogs.js
import React, { useState, useEffect } from 'react';
import { Table, Form, Button, InputGroup, Card, Badge, Spinner, Row, Col } from 'react-bootstrap';
import { Search, ShieldAlert, Cpu, Eye } from 'lucide-react';
import Swal from 'sweetalert2';
import { getUserActivityLogs } from '../../../services/adminApi';
import './UserActivityLogs.css'; 

const UserActivityLogs = () => {
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [actionTypeFilter, setActionTypeFilter] = useState('All');
    const [pagination, setPagination] = useState({ current_page: 1, last_page: 1, total: 0 });

    const fetchLogs = async (page = 1) => {
        setLoading(true);
        try {
            const params = {
                page,
                search,
                actionType: actionTypeFilter,
                per_page: 12
            };
            const response = await getUserActivityLogs(params);
            setLogs(response.data.data || []);
            setPagination({
                current_page: response.data.current_page || 1,
                last_page: response.data.last_page || 1,
                total: response.data.total || 0
            });
        } catch (err) {
            console.error('Could not load user activity logs.', err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const delayDebounce = setTimeout(() => {
            fetchLogs(1);
        }, 300);
        return () => clearTimeout(delayDebounce);
    }, [search, actionTypeFilter]);

    const handleOpenDetails = (log) => {
        Swal.fire({
            title: 'Activity Log Details',
            html: `
                <div style="text-align: left; font-size: 0.9rem;">
                    <p style="margin-bottom: 8px;"><strong>User Name:</strong> ${log.user_name}</p>
                    <p style="margin-bottom: 8px;"><strong>Email:</strong> ${log.email}</p>
                    <p style="margin-bottom: 8px;"><strong>Action:</strong> <span style="font-family: monospace; color: #10B981;">${log.action_type}</span></p>
                    <p style="margin-bottom: 8px;"><strong>IP Address:</strong> ${log.ip_address}</p>
                    <p style="margin-bottom: 8px;"><strong>Date & Time:</strong> ${new Date(log.created_at).toLocaleString('en-IN')}</p>
                    <p style="margin-bottom: 8px;"><strong>Browser/Device:</strong> <span style="font-size: 0.8rem; color: #9CA3AF;">${log.user_agent}</span></p>
                    <div style="margin-top: 15px; padding: 10px; background: #0D0E12; border-radius: 8px;">
                        <strong>Log Action Details:</strong>
                        <p style="margin-top: 5px; margin-bottom: 0; font-family: monospace; font-size: 0.85rem; color: #38BDF8;">${log.details}</p>
                    </div>
                </div>
            `,
            icon: 'info',
            background: '#151521',
            color: '#ffffff',
            confirmButtonColor: '#10B981',
            customClass: {
                popup: 'activity-logs-card text-white'
            }
        });
    };

    const getActionBadge = (action) => {
        if (action.includes('LOGIN_SUCCESS')) {
            return <Badge bg="success" className="px-2 py-1 text-white">Login Success</Badge>;
        }
        if (action.includes('LOGIN_FAILED')) {
            return <Badge bg="danger" className="px-2 py-1 text-white">Login Failed</Badge>;
        }
        return <Badge bg="secondary" className="px-2 py-1 text-white">{action}</Badge>;
    };

    return (
        <div className="container-fluid py-4 activity-logs-container">
            <div className="mb-4">
                <h2 className="activity-logs-title d-flex align-items-center gap-2">
                    <Cpu className="text-danger" size={28} /> User Activity Logs
                </h2>
                <p className="activity-logs-subtitle">Monitor secure authentication, login attempts, and full candidate sessions.</p>
            </div>

            <Card className="border-0 rounded-4 overflow-hidden mb-4 activity-logs-card">
                <Card.Body className="p-4 bg-transparent activity-logs-border-bottom">
                    <Row className="g-3">
                        <Col lg={8} md={6}>
                            <InputGroup className="border-0 rounded-3 activity-logs-input-group">
                                <InputGroup.Text className="bg-transparent border-0 text-secondary">
                                    <Search size={18} />
                                </InputGroup.Text>
                                <Form.Control
                                    type="text"
                                    placeholder="Search by User Name, Email, IP Address or Action Type..."
                                    className="bg-transparent border-0 shadow-none ps-0 activity-logs-input"
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                />
                            </InputGroup>
                        </Col>
                        <Col lg={4} md={6}>
                            <Form.Select className="border-0 rounded-3 text-white py-2 activity-logs-select" value={actionTypeFilter} onChange={(e) => setActionTypeFilter(e.target.value)}>
                                <option value="All" style={{ backgroundColor: '#151521' }}>All Actions</option>
                                <option value="LOGIN_SUCCESS" style={{ backgroundColor: '#151521' }}>Login Success</option>
                                <option value="LOGIN_FAILED" style={{ backgroundColor: '#151521' }}>Login Failed</option>
                            </Form.Select>
                        </Col>
                    </Row>
                </Card.Body>

                <div className="table-responsive">
                    <Table hover align="middle" className="m-0 text-nowrap table-dark table-borderless activity-logs-table">
                        <thead className="activity-logs-thead">
                            <tr className="activity-logs-thead-tr">
                                <th className="px-4 py-3 text-uppercase">#</th>
                                <th className="py-3 text-uppercase">User Name</th>
                                <th className="py-3 text-uppercase">Role</th>
                                <th className="py-3 text-uppercase">Email Address</th>
                                <th className="py-3 text-uppercase">Action Type</th>
                                <th className="py-3 text-uppercase">IP Address</th>
                                <th className="py-3 text-uppercase">Date & Time</th>
                                <th className="px-4 py-3 text-center text-uppercase">Action</th>
                            </tr>
                        </thead>
                        <tbody style={{ fontSize: '0.9rem' }}>
                            {loading ? (
                                <tr>
                                    <td colSpan="8" className="text-center p-5 activity-logs-text-secondary" style={{ backgroundColor: '#151521' }}>
                                        <Spinner animation="border" className="me-2" size="sm" /> Loading audit logs...
                                    </td>
                                </tr>
                            ) : logs.length > 0 ? (
                                logs.map((log, idx) => (
                                    <tr key={log.id} className="activity-logs-tbody-tr">
                                        <td className="px-4 activity-logs-text-secondary">
                                            {(pagination.current_page - 1) * 12 + idx + 1}
                                        </td>
                                        <td className="fw-semibold text-white">{log.user_name}</td>
                                        <td className="text-capitalize activity-logs-text-secondary small">{log.user_role}</td>
                                        <td className="activity-logs-text-secondary small">{log.email}</td>
                                        <td>{getActionBadge(log.action_type)}</td>
                                        <td className="activity-logs-text-secondary font-monospace small">{log.ip_address}</td>
                                        <td className="activity-logs-text-secondary small">{new Date(log.created_at).toLocaleString('en-IN')}</td>
                                                                                <td className="px-4 text-center">
                                            <div style={{ display: "flex", justifyContent: "center", alignItems: "center", width: "100%", margin: "0 auto" }}>
                                                {/* ✅ VIEW DETAILS BUTTON: ROYAL BLUE */}
                                                <button
                                                    type="button"
                                                    style={{
                                                        background: "#2563eb",
                                                        backgroundColor: "#2563eb",
                                                        color: "#ffffff",
                                                        border: "none",
                                                        borderRadius: "6px",
                                                        width: "32px",
                                                        height: "32px",
                                                        display: "inline-flex",
                                                        alignItems: "center",
                                                        justifyContent: "center",
                                                        cursor: "pointer",
                                                        padding: 0,
                                                        flexShrink: 0
                                                    }}
                                                    onClick={() => handleOpenDetails(log)}
                                                    title="View Activity Details"
                                                >
                                                    <Eye size={15} color="#ffffff" />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="8" className="text-center p-5 activity-logs-text-secondary" style={{ backgroundColor: '#151521' }}>
                                        <ShieldAlert size={36} className="mb-2" />
                                        <p className="m-0">No activity logs found matching the filters.</p>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </Table>
                </div>
            </Card>

            {/* Pagination Controls */}
            {pagination.last_page > 1 && (
                <div className="d-flex justify-content-between align-items-center p-3 rounded-4 shadow-sm" style={{ backgroundColor: '#151521', border: '1px solid #222531' }}>
                    <span className="activity-logs-text-secondary small">Showing Page {pagination.current_page} of {pagination.last_page} ({pagination.total} records)</span>
                    <div className="d-flex gap-2">
                        <Button variant="outline-secondary" className="btn-sm activity-logs-btn-secondary" disabled={pagination.current_page === 1} onClick={() => fetchLogs(pagination.current_page - 1)}>
                            Previous
                        </Button>
                        <Button variant="outline-secondary" className="btn-sm activity-logs-btn-secondary" disabled={pagination.current_page === pagination.last_page} onClick={() => fetchLogs(pagination.current_page + 1)}>
                            Next
                        </Button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default UserActivityLogs;